import { useQuery } from '@tanstack/react-query';
import { getProfileSchema } from '@tectonic/remix-client-network';
import { useEffect, useMemo, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useHaloScript, useSharedLocalState } from '../../../hooks';
import { queryKeys } from '../../../queryKeys';

import type {
  ElemasonUserProfileFormWidget,
  UserProfile,
  UserProfileData,
  UserProfileDataUpdate,
} from '@tectonic/types';
import type { ComponentProps } from 'react';
import type { ElemasonJsonSchemaForm } from '../../JSONSchemaForm';

const useUserProfile = (widget: ElemasonUserProfileFormWidget) => {
  const wData = widget.data!;

  const formRef = useRef<{ validateForm: () => unknown }>(null);
  const defaultName = useMemo(() => `${Date.now()}`, []);
  const name = useHaloScript<string>(wData.name);

  const stateKey = useHaloScript<string>(wData.stateKey)!;
  const profile = useHaloScript<UserProfile>(wData.formData)!;

  const {
    data: response,
    isLoading,
    isError,
  } = useQuery({
    queryKey: queryKeys.userProfileSchema(),
    queryFn: getProfileSchema,
  });

  const schema = response?.data?.schema;
  const uiSchema = response?.data?.uiSchema;

  const { setSharedState, clearSharedState } = useSharedLocalState<{
    data?: UserProfileDataUpdate;
    isValid?: boolean;
  }>(stateKey);

  const nFormData = profile?.profileData;

  const onChange: ComponentProps<typeof ElemasonJsonSchemaForm>['onChange'] =
    useDebouncedCallback((event) => {
      setSharedState({
        data: {
          name: name ?? profile?.name ?? defaultName,
          profileData: event.formData as UserProfileData,
          slug: profile?.slug,
        },
        isValid: !!formRef.current?.validateForm(),
      });
    }, 100);

  useEffect(
    () => () => {
      clearSharedState();
    },
    [clearSharedState]
  );

  return {
    isReady: !isLoading && !isError,
    uiSchema,
    schema,
    formData: nFormData,
    formRef,
    onChange,
  };
};

export { useUserProfile };
